import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import {
  financeApi,
  type AccountType,
  type CoaGroup,
  type CoaAccount,
  type CoaTreeResponse,
  type PartySubLedger,
  type PostingRules,
  type PostingRole,
  type RebuildGLSummary,
  type GstSettlementPreview,
} from '@/api/financeApi';
import { formatINR } from '@/utils/format';

const typeColors: Record<string, string> = {
  ASSET: 'bg-blue-100 text-blue-800',
  LIABILITY: 'bg-red-100 text-red-800',
  INCOME: 'bg-green-100 text-green-800',
  EXPENSE: 'bg-amber-100 text-amber-800',
  EQUITY: 'bg-purple-100 text-purple-800',
};

const typeOptions: (AccountType | 'ALL')[] = ['ALL', 'ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

const naturalBalance = (type: AccountType): 'DEBIT' | 'CREDIT' =>
  type === 'ASSET' || type === 'EXPENSE' ? 'DEBIT' : 'CREDIT';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const inputCls = 'w-full h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] disabled:bg-slate-50 disabled:text-slate-400';
const labelCls = 'block text-xs font-semibold text-slate-500 mb-1';
const secondaryBtn = 'flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50';

interface AccountForm {
  code: string;
  name: string;
  groupId: string;
  balanceType: 'DEBIT' | 'CREDIT';
  openingBalance: number;
  allowDirectPosting: boolean;
  isActive: boolean;
  description: string;
}

interface GroupForm {
  code: string;
  name: string;
  type: AccountType;
  parentId: string;
}

const emptyAccountForm: AccountForm = {
  code: '',
  name: '',
  groupId: '',
  balanceType: 'DEBIT',
  openingBalance: 0,
  allowDirectPosting: true,
  isActive: true,
  description: '',
};

export default function ChartOfAccountsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [view, setView] = useState<'accounts' | 'rules'>('accounts');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CoaTreeResponse | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AccountType | 'ALL'>('ALL');
  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedParties, setExpandedParties] = useState<Set<string>>(new Set());

  const [slideOpen, setSlideOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<CoaAccount | null>(null);
  const [form, setForm] = useState<AccountForm>(emptyAccountForm);

  const [groupsSlideOpen, setGroupsSlideOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CoaGroup | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm>({ code: '', name: '', type: 'ASSET', parentId: '' });

  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; variant?: 'danger' | 'warning'; onConfirm: () => void } | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildGLSummary | null>(null);
  const [gstOpen, setGstOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await financeApi.getCoaTree();
      setData(res);
      setExpanded((prev) => {
        if (prev.size > 0) return prev;
        // First load: expand level 1 and 2 groups
        return new Set(res.groups.filter((g) => g.level <= 2).map((g) => g.id));
      });
    } catch (err: any) {
      console.error('Failed to load Chart of Accounts:', err);
      toast.error(err.response?.data?.message || 'Failed to load Chart of Accounts');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const groupsById = useMemo(() => new Map((data?.groups || []).map((g) => [g.id, g])), [data]);

  // ── Account slide-over ───────────────────────────────────────────────────
  const suggestCode = async (groupId: string) => {
    try {
      const nextCode = await financeApi.suggestCode(groupId);
      setForm((p) => ({ ...p, code: nextCode }));
    } catch {
      // keep whatever the user typed
    }
  };

  const openAddAccount = (groupId?: string) => {
    const group = (groupId && groupsById.get(groupId)) || data?.groups.find((g) => g.level === 3) || data?.groups[0];
    setEditingAccount(null);
    setForm({ ...emptyAccountForm, groupId: group?.id || '', balanceType: group ? naturalBalance(group.type) : 'DEBIT' });
    if (group) suggestCode(group.id);
    setSlideOpen(true);
  };

  const openEditAccount = (acc: CoaAccount) => {
    setEditingAccount(acc);
    setForm({
      code: acc.code,
      name: acc.name,
      groupId: acc.groupId,
      balanceType: acc.balanceType,
      openingBalance: acc.openingBalance,
      allowDirectPosting: acc.allowDirectPosting,
      isActive: acc.isActive,
      description: acc.description || '',
    });
    setSlideOpen(true);
  };

  const handleGroupChange = (groupId: string) => {
    const group = groupsById.get(groupId);
    setForm((p) => ({
      ...p,
      groupId,
      balanceType: editingAccount ? p.balanceType : group ? naturalBalance(group.type) : p.balanceType,
    }));
    if (!editingAccount) suggestCode(groupId);
  };

  const handleSaveAccount = async () => {
    if (!form.name.trim()) {
      toast.error('Account name is required');
      return;
    }
    if (!form.groupId) {
      toast.error('Please select an account group');
      return;
    }
    setSaving(true);
    try {
      if (editingAccount) {
        await financeApi.updateAccount(editingAccount.id, {
          groupId: form.groupId,
          code: editingAccount.isSystem ? undefined : form.code.trim(),
          name: form.name.trim(),
          balanceType: form.balanceType,
          openingBalance: Number(form.openingBalance) || 0,
          allowDirectPosting: form.allowDirectPosting,
          isActive: form.isActive,
          description: form.description,
        });
        toast.success(`Account "${form.name}" updated`);
      } else {
        await financeApi.createAccount({
          groupId: form.groupId,
          code: form.code || undefined,
          name: form.name.trim(),
          balanceType: form.balanceType,
          openingBalance: Number(form.openingBalance) || 0,
          allowDirectPosting: form.allowDirectPosting,
          isActive: form.isActive,
          description: form.description,
        });
        toast.success(`Account "${form.name}" created successfully`);
      }
      setSlideOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = (acc: CoaAccount) => {
    if (acc.isSystem) {
      toast.error('System accounts are protected and cannot be deleted.');
      return;
    }
    setConfirm({
      title: 'Delete Account',
      message: `Delete account "${acc.name}" (${acc.code})? This cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await financeApi.deleteAccount(acc.id);
          toast.success(`Account "${acc.name}" deleted`);
          loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || 'Failed to delete account');
        }
      },
    });
  };

  // ── Group slide-over ─────────────────────────────────────────────────────
  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ code: '', name: '', type: 'ASSET', parentId: '' });
    setGroupsSlideOpen(true);
  };

  const openEditGroup = (g: CoaGroup) => {
    setEditingGroup(g);
    setGroupForm({ code: g.code, name: g.name, type: g.type, parentId: g.parentId || '' });
    setGroupsSlideOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    if (!groupForm.code.trim()) {
      toast.error('Group code is required');
      return;
    }
    setSaving(true);
    try {
      if (editingGroup) {
        await financeApi.updateGroup(editingGroup.id, {
          name: groupForm.name.trim(),
          code: editingGroup.isSystem ? undefined : groupForm.code.trim(),
          parentId: editingGroup.isSystem ? undefined : groupForm.parentId || undefined,
        });
        toast.success(`Group "${groupForm.name}" updated`);
      } else {
        await financeApi.createGroup({
          code: groupForm.code.trim(),
          name: groupForm.name.trim(),
          type: groupForm.type,
          parentId: groupForm.parentId || undefined,
        });
        toast.success(`Group "${groupForm.name}" created successfully`);
      }
      setGroupsSlideOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save account group');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = (g: CoaGroup) => {
    setConfirm({
      title: 'Delete Group',
      message: `Delete group "${g.name}" (${g.code})? Only empty groups can be deleted.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await financeApi.deleteGroup(g.id);
          toast.success(`Group "${g.name}" deleted`);
          loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || 'Failed to delete group');
        }
      },
    });
  };

  // ── GL maintenance ───────────────────────────────────────────────────────
  const handleRebuildGL = () => {
    setConfirm({
      title: 'Rebuild General Ledger',
      message:
        'Re-post every invoice, GRN, payment, return, challan, stock entry and production order with the current posting rules. Manual journal vouchers and closed fiscal years are not touched. Continue?',
      variant: 'warning',
      onConfirm: async () => {
        setConfirm(null);
        setRebuilding(true);
        try {
          const res = await financeApi.rebuildGL();
          setRebuildResult(res.data);
          toast.success(res.message);
          loadData();
        } catch (err: any) {
          toast.error(err.response?.data?.message || 'Failed to rebuild General Ledger');
        } finally {
          setRebuilding(false);
        }
      },
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePartyExpand = (accId: string) => {
    setExpandedParties((prev) => {
      const next = new Set(prev);
      if (next.has(accId)) next.delete(accId);
      else next.add(accId);
      return next;
    });
  };

  const summary = data?.summary || {
    totalAssets: 0,
    totalLiabilities: 0,
    totalEquity: 0,
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    equationBalanced: true,
  };

  // Groups in tree order (parent before children). While searching, matching groups
  // and their ancestors are shown expanded; otherwise collapsed groups hide descendants.
  const visibleGroups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const matches = (g: CoaGroup) =>
      g.code.toLowerCase().includes(q) ||
      g.name.toLowerCase().includes(q) ||
      data.accounts.some((a) => a.groupId === g.id && (a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q)));

    const subtreeMatches = (g: CoaGroup): boolean => matches(g) || (g.children || []).some(subtreeMatches);
    const out: CoaGroup[] = [];
    const walk = (nodes: CoaGroup[], parentOpen: boolean) => {
      [...nodes]
        .sort((a, b) => a.code.localeCompare(b.code))
        .forEach((g) => {
          if (typeFilter !== 'ALL' && g.type !== typeFilter) return;
          if (q && !subtreeMatches(g)) return;
          if (!q && !parentOpen) return;
          out.push(g);
          walk(g.children || [], q ? true : parentOpen && expanded.has(g.id));
        });
    };
    walk(data.tree, true);
    return out;
  }, [data, search, typeFilter, expanded]);

  const getBalanceColor = (a: CoaAccount) => {
    if (a.type === 'ASSET') return a.currentBalance >= 0 ? 'text-blue-700' : 'text-red-600';
    if (a.type === 'LIABILITY') return a.currentBalance >= 0 ? 'text-red-600' : 'text-green-700';
    if (a.type === 'INCOME') return 'text-green-700';
    if (a.type === 'EXPENSE') return 'text-amber-700';
    return 'text-slate-800';
  };

  // Parent options for a group: same type, not itself or one of its descendants
  const parentOptions = useMemo(() => {
    if (!data) return [];
    const blocked = new Set<string>();
    if (editingGroup) {
      const collect = (g: CoaGroup) => {
        blocked.add(g.id);
        (g.children || []).forEach(collect);
      };
      const node = groupsById.get(editingGroup.id);
      const findInTree = (nodes: CoaGroup[]): CoaGroup | undefined => {
        for (const n of nodes) {
          if (n.id === editingGroup.id) return n;
          const f = findInTree(n.children || []);
          if (f) return f;
        }
        return undefined;
      };
      collect(findInTree(data.tree) || node || editingGroup);
    }
    return data.groups.filter(
      (g) => g.level <= 3 && !blocked.has(g.id) && (!editingGroup || g.type === editingGroup.type)
    );
  }, [data, editingGroup, groupsById]);

  const accountGroupOptions = useMemo(() => {
    if (!data) return [];
    return editingAccount ? data.groups.filter((g) => g.type === editingAccount.type) : data.groups;
  }, [data, editingAccount]);

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap px-6 pt-6">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Chart of Accounts (COA)</h1>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                  Double-Entry
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Standard 4-level Indian ERP structure with automated transaction auto-posting and party sub-ledgers.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => navigate('/finance/trial-balance')} className={secondaryBtn}>
                <i className="ri-scales-3-line text-sm text-primary-600" /> Trial Balance
              </button>
              <button onClick={() => navigate('/finance/journal')} className={secondaryBtn}>
                <i className="ri-file-list-3-line text-sm text-indigo-600" /> Journal Vouchers
              </button>
              <button onClick={() => setGstOpen(true)} className={secondaryBtn}>
                <i className="ri-percent-line text-sm text-emerald-600" /> GST Set-off
              </button>
              <button onClick={handleRebuildGL} disabled={rebuilding} className={secondaryBtn}>
                <i className={`${rebuilding ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-sm text-slate-500`} />
                {rebuilding ? 'Rebuilding…' : 'Rebuild GL'}
              </button>
              <button onClick={openAddGroup} className={secondaryBtn}>
                <i className="ri-folder-settings-line text-sm text-slate-500" /> Add Group
              </button>
              <button
                onClick={() => openAddAccount()}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-sm" /> Add Account
              </button>
            </div>
          </div>

          {/* Summary Metric Cards */}
          <div className="px-6 pt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Assets</p>
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold">A</span>
              </div>
              <p className="text-xl font-bold text-blue-700">{formatINR(summary.totalAssets)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Cash, Bank, Inventory & Debtors</p>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-red-300 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Liabilities</p>
                <span className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-xs font-bold">L</span>
              </div>
              <p className="text-xl font-bold text-red-600">{formatINR(summary.totalLiabilities)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Creditors, Taxes & Loans</p>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-purple-300 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Equity</p>
                <span className="w-6 h-6 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center text-xs font-bold">E</span>
              </div>
              <p className="text-xl font-bold text-purple-700">{formatINR(summary.totalEquity)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Capital & Retained Earnings</p>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-green-300 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Operating Revenue</p>
                <span className="w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-xs font-bold">I</span>
              </div>
              <p className="text-xl font-bold text-green-700">{formatINR(summary.totalIncome)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Sales & Direct/Indirect Income</p>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-emerald-300 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Net Profit / (Loss)</p>
                <i className={`ri-arrow-${summary.netProfit >= 0 ? 'up' : 'down'}-circle-line text-sm ${summary.netProfit >= 0 ? 'text-green-600' : 'text-red-500'}`} />
              </div>
              <p className={`text-xl font-bold ${summary.netProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {formatINR(summary.netProfit)}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">Revenue minus Expenses</p>
            </div>
          </div>

          {/* Double-Entry Verification Alert Banner */}
          <div className="px-6 pt-5">
            <div
              className={`border rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap transition-all ${
                summary.equationBalanced ? 'bg-emerald-50/70 border-emerald-300 text-emerald-900' : 'bg-rose-50 border-rose-300 text-rose-900'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <i className={`text-lg ${summary.equationBalanced ? 'ri-checkbox-circle-fill text-emerald-600' : 'ri-alert-fill text-rose-600'}`} />
                <span className="text-xs font-semibold">
                  {summary.equationBalanced ? 'Double-Entry Bookkeeping Invariant Verified: ' : 'Accounting Imbalance Detected: '}
                  <span className="font-bold">
                    Assets ({formatINR(summary.totalAssets)}) = Liabilities ({formatINR(summary.totalLiabilities)}) + Equity & Profit ({formatINR(summary.totalEquity + summary.netProfit)})
                  </span>
                </span>
              </div>
              <span className="text-[11px] font-medium opacity-80">
                {summary.equationBalanced ? 'Books in Balance ✅' : 'Out of balance by ' + formatINR(Math.abs(summary.totalAssets - (summary.totalLiabilities + summary.totalEquity + summary.netProfit)))}
              </span>
            </div>
          </div>

          {/* View Tabs */}
          <div className="px-6 pt-5">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit border border-[#e2e8f0]">
              {([
                { key: 'accounts', label: 'Accounts', icon: 'ri-git-branch-line' },
                { key: 'rules', label: 'Posting Rules', icon: 'ri-route-line' },
              ] as const).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setView(t.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                    view === t.key ? 'bg-white text-[#1e293b] shadow-sm' : 'text-slate-600 hover:text-[#1e293b]'
                  }`}
                >
                  <i className={`${t.icon} text-sm`} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {view === 'rules' ? (
            <PostingRulesPanel accounts={data?.accounts || []} onRebuild={handleRebuildGL} rebuilding={rebuilding} />
          ) : (
            <>
              {/* Filter Bar */}
              <div className="px-6 pt-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search code, account, or group..."
                      className="w-72 h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] placeholder:text-slate-400"
                    />
                  </div>

                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as AccountType | 'ALL')}
                    className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer"
                  >
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t === 'ALL' ? 'All Account Types' : t.charAt(0) + t.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>

                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-[#e2e8f0] text-primary-600 focus:ring-0"
                    />
                    Show Inactive
                  </label>
                </div>

                <button
                  onClick={() => {
                    if (data) {
                      const allIds = new Set(data.groups.map((g) => g.id));
                      setExpanded(expanded.size === allIds.size ? new Set() : allIds);
                    }
                  }}
                  className="text-xs text-slate-600 hover:text-primary-600 font-medium px-2 py-1 cursor-pointer"
                >
                  {data && expanded.size === data.groups.length ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              {/* Chart of Accounts Tree Table */}
              <div className="px-6 pt-5">
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-x-auto">
                  {loading && !data ? (
                    <div className="py-16 text-center text-slate-400 flex flex-col items-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
                      <p className="text-xs">Loading Chart of Accounts...</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs min-w-[900px]">
                      <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                        <tr>
                          <th className="text-left px-5 py-3 w-28">Code</th>
                          <th className="text-left px-4 py-3">Account / Group Name</th>
                          <th className="text-left px-4 py-3 w-28">Type</th>
                          <th className="text-center px-4 py-3 w-24">Balance Type</th>
                          <th className="text-right px-4 py-3 w-36">Current Balance</th>
                          <th className="text-right px-4 py-3 w-32">Opening</th>
                          <th className="text-center px-4 py-3 w-36">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {visibleGroups.map((group) => {
                          const isOpen = search.trim() !== '' || expanded.has(group.id);
                          const accounts = (data?.accounts || []).filter(
                            (a) => a.groupId === group.id && (showInactive || a.isActive)
                          );
                          const groupIndent = (group.level - 1) * 20;
                          const hasChildren = (group.children || []).length > 0 || accounts.length > 0;

                          return (
                            <div key={group.id} style={{ display: 'contents' }}>
                              {/* Group Row */}
                              <tr
                                className={`border-b border-slate-100 cursor-pointer transition-colors ${
                                  group.level === 1
                                    ? 'bg-slate-100/60 font-bold hover:bg-slate-200/50'
                                    : group.level === 2
                                    ? 'bg-slate-50/70 font-semibold hover:bg-slate-100/50'
                                    : 'bg-white hover:bg-slate-50/80 font-medium'
                                }`}
                                onClick={() => toggleExpand(group.id)}
                              >
                                <td className="px-5 py-2.5 font-mono text-slate-800 font-bold">{group.code}</td>
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-2" style={{ paddingLeft: `${groupIndent}px` }}>
                                    <i
                                      className={`${isOpen ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-sm ${
                                        hasChildren ? 'text-slate-400' : 'text-slate-200'
                                      }`}
                                    />
                                    <span className="text-[#1e293b]">{group.name}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200/70 text-slate-500 font-normal">
                                      Level {group.level}
                                    </span>
                                    {group.isSystem && (
                                      <i className="ri-shield-check-line text-slate-400 text-xs" title="System Group" />
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${typeColors[group.type]}`}>
                                    {group.type}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-center text-slate-400 font-mono text-[10px]">—</td>
                                <td className="px-4 py-2.5 text-right font-bold text-[#1e293b]">
                                  {formatINR(group.totalBalance || 0)}
                                </td>
                                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">—</td>
                                <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => openAddAccount(group.id)}
                                      className="text-[11px] text-primary-600 hover:text-primary-800 font-semibold px-2 py-0.5 rounded hover:bg-primary-50 transition-colors cursor-pointer"
                                      title="Add an account in this group"
                                    >
                                      + Account
                                    </button>
                                    <button
                                      onClick={() => openEditGroup(group)}
                                      className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-[#1e293b] transition-colors cursor-pointer"
                                      title="Edit group"
                                    >
                                      <i className="ri-pencil-line text-sm" />
                                    </button>
                                    {!group.isSystem && (
                                      <button
                                        onClick={() => handleDeleteGroup(group)}
                                        className="p-1 rounded text-slate-500 hover:bg-red-100 hover:text-red-700 transition-colors cursor-pointer"
                                        title="Delete group"
                                      >
                                        <i className="ri-delete-bin-line text-sm" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {/* Accounts under this group */}
                              {isOpen &&
                                accounts.map((a) => {
                                  const hasParties = a.subParties && a.subParties.length > 0;
                                  const isPartyOpen = expandedParties.has(a.id);

                                  return (
                                    <div key={a.id} style={{ display: 'contents' }}>
                                      <tr className={`border-b border-slate-100 hover:bg-primary-50/30 transition-colors ${a.isActive ? '' : 'opacity-60'}`}>
                                        <td className="px-5 py-2 font-mono text-slate-600 pl-10 text-[11px]">{a.code}</td>
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-2 flex-wrap" style={{ paddingLeft: `${groupIndent + 24}px` }}>
                                            <span className="font-medium text-[#1e293b]">{a.name}</span>
                                            {a.isSystem && (
                                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-[#e2e8f0]">
                                                System
                                              </span>
                                            )}
                                            {!a.allowDirectPosting && (
                                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Updated automatically by its module; not available in manual journals">
                                                Auto
                                              </span>
                                            )}
                                            {!a.isActive && (
                                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-[#e2e8f0]">
                                                Inactive
                                              </span>
                                            )}
                                            {hasParties && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  togglePartyExpand(a.id);
                                                }}
                                                className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 font-semibold flex items-center gap-1 transition-colors cursor-pointer"
                                              >
                                                <i className={isPartyOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                                                {a.subParties?.length} Parties
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-2">
                                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeColors[a.type]}`}>
                                            {a.type}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                          <span
                                            className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                                              a.balanceType === 'DEBIT' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                                            }`}
                                          >
                                            {a.balanceType}
                                          </span>
                                        </td>
                                        <td className={`px-4 py-2 text-right font-bold ${getBalanceColor(a)}`}>
                                          {formatINR(a.currentBalance)}
                                        </td>
                                        <td className="px-4 py-2 text-right text-slate-500 font-mono">
                                          {formatINR(a.openingBalance)}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <button
                                              onClick={() => navigate(`/finance/ledger?accountId=${a.id}`)}
                                              className="p-1 rounded text-slate-500 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                                              title="View General Ledger"
                                            >
                                              <i className="ri-book-open-line text-sm" />
                                            </button>
                                            {a.allowDirectPosting && a.isActive && (
                                              <button
                                                onClick={() => navigate(`/finance/journal/new?accountId=${a.id}`)}
                                                className="p-1 rounded text-slate-500 hover:bg-indigo-100 hover:text-indigo-700 transition-colors cursor-pointer"
                                                title="New Journal Entry"
                                              >
                                                <i className="ri-file-add-line text-sm" />
                                              </button>
                                            )}
                                            <button
                                              onClick={() => openEditAccount(a)}
                                              className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-[#1e293b] transition-colors cursor-pointer"
                                              title="Edit Account"
                                            >
                                              <i className="ri-pencil-line text-sm" />
                                            </button>
                                            {!a.isSystem && (
                                              <button
                                                onClick={() => handleDeleteAccount(a)}
                                                className="p-1 rounded text-slate-500 hover:bg-red-100 hover:text-red-700 transition-colors cursor-pointer"
                                                title="Delete Account"
                                              >
                                                <i className="ri-delete-bin-line text-sm" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>

                                      {/* Sub-Parties (Customers / Vendors Sub-Ledger) */}
                                      {isPartyOpen &&
                                        hasParties &&
                                        a.subParties?.map((p: PartySubLedger) => (
                                          <tr key={p.id} className="bg-blue-50/30 border-b border-blue-100/50 text-slate-700">
                                            <td className="px-5 py-1.5 pl-14 font-mono text-[10px] text-slate-400">PARTY</td>
                                            <td className="px-4 py-1.5 pl-16">
                                              <div className="flex items-center gap-2">
                                                <i className="ri-user-3-line text-blue-500 text-xs" />
                                                <span className="font-semibold text-[#1e293b]">{p.name}</span>
                                                {p.phone && <span className="text-[10px] text-slate-400">{p.phone}</span>}
                                                {p.pan && <span className="text-[10px] font-mono text-slate-500 bg-white px-1 rounded border">PAN: {p.pan}</span>}
                                              </div>
                                            </td>
                                            <td className="px-4 py-1.5 text-[10px] text-slate-500">Sub-Ledger</td>
                                            <td className="px-4 py-1.5 text-center text-[10px] font-mono">{p.balanceType}</td>
                                            <td className="px-4 py-1.5 text-right font-semibold text-[#1e293b]">{formatINR(p.currentBalance)}</td>
                                            <td className="px-4 py-1.5 text-right text-slate-400 font-mono text-[10px]">—</td>
                                            <td className="px-4 py-1.5 text-center">
                                              <button
                                                onClick={() => navigate(`/finance/ledger?partyId=${p.id}&accountId=${a.id}`)}
                                                className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold px-2 py-0.5 rounded hover:bg-blue-100 cursor-pointer"
                                              >
                                                Statement
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                    </div>
                                  );
                                })}
                            </div>
                          );
                        })}

                        {visibleGroups.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-12 text-center text-slate-400">
                              No matching accounts or groups found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Add / Edit Account Slide-over */}
        {slideOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setSlideOpen(false)} />
            <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 overflow-y-auto shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b]">{editingAccount ? 'Edit Ledger Account' : 'Add Ledger Account'}</h2>
                  <p className="text-xs text-slate-500">
                    {editingAccount
                      ? `${editingAccount.code} — ${editingAccount.name}${editingAccount.isSystem ? ' (system account)' : ''}`
                      : 'Create a leaf account in the Chart of Accounts'}
                  </p>
                </div>
                <button
                  onClick={() => setSlideOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4 flex-1">
                <div>
                  <label className={labelCls}>
                    Account Group <span className="text-red-500">*</span>
                  </label>
                  <select value={form.groupId} onChange={(e) => handleGroupChange(e.target.value)} className={inputCls}>
                    {accountGroupOptions.map((g) => (
                      <option key={g.id} value={g.id}>
                        {' '.repeat((g.level - 1) * 3)}
                        {g.code} — {g.name} ({g.type})
                      </option>
                    ))}
                  </select>
                  {editingAccount && (
                    <p className="text-[11px] text-slate-400 mt-1">An account can move to any other {editingAccount.type.toLowerCase()} group.</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Account Code</label>
                    <input
                      type="text"
                      value={form.code}
                      disabled={!!editingAccount?.isSystem}
                      onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                      placeholder="Auto-suggested"
                      className={`${inputCls} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Normal Balance</label>
                    <select
                      value={form.balanceType}
                      onChange={(e) => setForm((p) => ({ ...p, balanceType: e.target.value as 'DEBIT' | 'CREDIT' }))}
                      className={inputCls}
                    >
                      <option value="DEBIT">Debit (Dr)</option>
                      <option value="CREDIT">Credit (Cr)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Account Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Office Stationery, ICICI Current A/c"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Opening Balance (₹)</label>
                  <input
                    type="number"
                    value={form.openingBalance}
                    onChange={(e) => setForm((p) => ({ ...p, openingBalance: parseFloat(e.target.value) || 0 }))}
                    className={inputCls}
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Entered on the account's normal balance side. The net of all opening balances is held in Opening Balance Equity automatically.
                  </p>
                </div>

                <div>
                  <label className={labelCls}>Description / Notes</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Optional details or purpose of this account"
                    className="w-full px-3 py-2 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b]"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div>
                    <p className="text-xs font-semibold text-slate-800">Allow Direct Journal Posting</p>
                    <p className="text-[11px] text-slate-400">Can be selected in manual journal vouchers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.allowDirectPosting}
                    onChange={(e) => setForm((p) => ({ ...p, allowDirectPosting: e.target.checked }))}
                    className="w-4 h-4 text-primary-600 rounded cursor-pointer"
                  />
                </div>

                {editingAccount && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div>
                      <p className="text-xs font-semibold text-slate-800">Active</p>
                      <p className="text-[11px] text-slate-400">Inactive accounts are hidden from selection and reports</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                      className="w-4 h-4 text-primary-600 rounded cursor-pointer"
                    />
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setSlideOpen(false)}
                  className="h-9 px-4 text-sm font-medium text-slate-700 border border-[#e2e8f0] bg-white hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAccount}
                  disabled={saving}
                  className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editingAccount ? 'Save Changes' : 'Save Account'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Add / Edit Account Group Slide-over */}
        {groupsSlideOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setGroupsSlideOpen(false)} />
            <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 overflow-y-auto shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b]">{editingGroup ? 'Edit Account Group' : 'Add Account Group'}</h2>
                  <p className="text-xs text-slate-500">
                    {editingGroup
                      ? `${editingGroup.code} — ${editingGroup.name}${editingGroup.isSystem ? ' (system group: name only)' : ''}`
                      : 'Create a sub-group under the 5 core accounting categories'}
                  </p>
                </div>
                <button
                  onClick={() => setGroupsSlideOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4 flex-1">
                <div>
                  <label className={labelCls}>Parent Group</label>
                  <select
                    value={groupForm.parentId}
                    disabled={!!editingGroup?.isSystem}
                    onChange={(e) => {
                      const p = groupsById.get(e.target.value);
                      setGroupForm((prev) => ({ ...prev, parentId: e.target.value, type: p?.type || prev.type }));
                    }}
                    className={inputCls}
                  >
                    {!editingGroup && <option value="">— None (new top-level category) —</option>}
                    {editingGroup?.isSystem && !editingGroup.parentId && <option value="">— Top level —</option>}
                    {parentOptions.map((g) => (
                      <option key={g.id} value={g.id}>
                        {' '.repeat((g.level - 1) * 3)}
                        {g.code} — {g.name} ({g.type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>
                      Group Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={groupForm.code}
                      disabled={!!editingGroup?.isSystem}
                      onChange={(e) => setGroupForm((p) => ({ ...p, code: e.target.value }))}
                      placeholder="e.g. 5250"
                      className={`${inputCls} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Account Type</label>
                    <select
                      value={groupForm.type}
                      disabled={!!groupForm.parentId || !!editingGroup}
                      onChange={(e) => setGroupForm((p) => ({ ...p, type: e.target.value as AccountType }))}
                      className={inputCls}
                    >
                      <option value="ASSET">Asset</option>
                      <option value="LIABILITY">Liability</option>
                      <option value="EQUITY">Equity</option>
                      <option value="INCOME">Income</option>
                      <option value="EXPENSE">Expense</option>
                    </select>
                    {groupForm.parentId && !editingGroup && (
                      <p className="text-[11px] text-slate-400 mt-1">Inherited from the parent group.</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Marketing & Advertising Expenses"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setGroupsSlideOpen(false)}
                  className="h-9 px-4 text-sm font-medium text-slate-700 border border-[#e2e8f0] bg-white hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGroup}
                  disabled={saving}
                  className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editingGroup ? 'Save Changes' : 'Save Group'}
                </button>
              </div>
            </div>
          </>
        )}

        {gstOpen && (
          <GstSettlementModal
            bankAccounts={(data?.accounts || []).filter((a) => a.type === 'ASSET' && a.isActive)}
            onClose={() => setGstOpen(false)}
            onPosted={() => {
              setGstOpen(false);
              loadData();
            }}
          />
        )}

        {rebuildResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#e2e8f0]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
                <h2 className="text-base font-bold text-[#1e293b]">General Ledger Rebuilt</h2>
                <button
                  onClick={() => setRebuildResult(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-4 gap-3 text-center">
                  {[
                    { label: 'Documents', value: rebuildResult.documents, cls: 'text-[#1e293b]' },
                    { label: 'Posted', value: rebuildResult.posted, cls: 'text-emerald-600' },
                    { label: 'Removed', value: rebuildResult.removed, cls: 'text-amber-600' },
                    { label: 'Errors', value: rebuildResult.errors.length, cls: rebuildResult.errors.length ? 'text-red-600' : 'text-slate-400' },
                  ].map((s) => (
                    <div key={s.label} className="bg-slate-50 rounded-lg p-3 border border-[#e2e8f0]">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{s.label}</p>
                      <p className={`text-xl font-bold mt-0.5 ${s.cls}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  {rebuildResult.skipped} documents had nothing to post (drafts, cancelled, zero value or dated in a closed fiscal year).
                </p>
                {rebuildResult.errors.length > 0 && (
                  <div className="max-h-56 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100">
                    {rebuildResult.errors.map((e) => (
                      <div key={`${e.table}-${e.id}`} className="px-3 py-2 text-xs">
                        <p className="font-semibold text-red-700">{e.table.replace(/_/g, ' ')}</p>
                        <p className="text-slate-600">{e.error}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50 flex justify-end">
                <button
                  onClick={() => setRebuildResult(null)}
                  className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          open={!!confirm}
          title={confirm?.title || ''}
          message={confirm?.message || ''}
          variant={confirm?.variant}
          onConfirm={() => confirm?.onConfirm()}
          onCancel={() => setConfirm(null)}
        />
      </div>
    </AppLayout>
  );
}

// ── Posting Rules ────────────────────────────────────────────────────────────

function PostingRulesPanel({
  accounts,
  onRebuild,
  rebuilding,
}: {
  accounts: CoaAccount[];
  onRebuild: () => void;
  rebuilding: boolean;
}) {
  const toast = useToast();
  const [rules, setRules] = useState<PostingRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState('ALL');

  useEffect(() => {
    let cancelled = false;
    financeApi
      .getPostingRules()
      .then((r) => !cancelled && setRules(r))
      .catch((err: any) => toast.error(err.response?.data?.message || 'Failed to load posting rules'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const roleByKey = useMemo(() => new Map((rules?.roles || []).map((r) => [r.key, r])), [rules]);
  const modules = useMemo(() => ['ALL', ...new Set((rules?.events || []).map((e) => e.module))], [rules]);
  const roleGroups = useMemo(() => {
    const map = new Map<string, PostingRole[]>();
    for (const r of rules?.roles || []) {
      if (!map.has(r.group)) map.set(r.group, []);
      map.get(r.group)!.push(r);
    }
    return [...map.entries()];
  }, [rules]);
  const missing = (rules?.roles || []).filter((r) => r.source === 'MISSING');

  const changeMapping = async (role: PostingRole, accountId: string) => {
    setSavingRole(role.key);
    try {
      const next =
        accountId === role.defaultAccountId && role.source === 'MAPPED'
          ? await financeApi.resetPostingRule(role.key)
          : await financeApi.setPostingRule(role.key, accountId);
      setRules(next);
      toast.success(`${role.label} now posts to the selected account`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update posting rule');
    } finally {
      setSavingRole(null);
    }
  };

  const resetMapping = async (role: PostingRole) => {
    setSavingRole(role.key);
    try {
      setRules(await financeApi.resetPostingRule(role.key));
      toast.success(`${role.label} reset to its default account`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reset posting rule');
    } finally {
      setSavingRole(null);
    }
  };

  if (loading) {
    return (
      <div className="px-6 pt-5">
        <div className="bg-white border border-[#e2e8f0] rounded-xl py-16 text-center text-slate-400 flex flex-col items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
          <p className="text-xs">Loading posting rules...</p>
        </div>
      </div>
    );
  }
  if (!rules) return null;

  const accountLabel = (r: PostingRole) =>
    r.accountId ? `${r.accountCode} — ${r.accountName}` : 'Not mapped';

  return (
    <div className="px-6 pt-5 space-y-5">
      <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl px-4 py-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2.5">
          <i className="ri-information-line text-indigo-600 text-lg mt-0.5" />
          <p className="text-xs text-indigo-900 leading-relaxed max-w-3xl">
            Every automatic posting debits and credits a <b>role</b> (Bank, Debtors, Raw Material Stock, …). Choose which ledger account each role
            uses below — the change applies to new transactions immediately. Use <b>Rebuild GL</b> to re-post existing documents with the new mapping.
          </p>
        </div>
        <button onClick={onRebuild} disabled={rebuilding} className={secondaryBtn}>
          <i className={`${rebuilding ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-sm text-slate-500`} />
          {rebuilding ? 'Rebuilding…' : 'Rebuild GL'}
        </button>
      </div>

      {missing.length > 0 && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 rounded-xl px-4 py-2.5 text-xs flex items-center gap-2">
          <i className="ri-alert-fill text-rose-600 text-base" />
          <span>
            <b>{missing.length} role(s) have no account:</b> {missing.map((m) => m.label).join(', ')}. Postings that need them will be skipped until they are mapped.
          </span>
        </div>
      )}

      {/* Role → account mapping */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
          <p className="text-sm font-semibold text-slate-800">Account Mapping</p>
          <p className="text-xs text-slate-400">Which ledger account each posting role uses</p>
        </div>
        <table className="w-full text-xs min-w-[820px]">
          <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
            <tr>
              <th className="text-left px-5 py-2.5 w-64">Posting Role</th>
              <th className="text-left px-4 py-2.5">Used For</th>
              <th className="text-left px-4 py-2.5 w-80">Ledger Account</th>
              <th className="text-center px-4 py-2.5 w-24">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {roleGroups.map(([groupName, roles]) => (
              <div key={groupName} style={{ display: 'contents' }}>
                <tr className="bg-slate-50/70">
                  <td colSpan={4} className="px-5 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {groupName}
                  </td>
                </tr>
                {roles.map((r) => {
                  const options = accounts.filter((a) => a.isActive && r.allowedTypes.includes(a.type));
                  return (
                    <tr key={r.key} className="hover:bg-slate-50/60">
                      <td className="px-5 py-2.5">
                        <p className="font-semibold text-[#1e293b]">{r.label}</p>
                        <p className="text-[10px] font-mono text-slate-400">{r.key}</p>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{r.description}</td>
                      <td className="px-4 py-2.5">
                        <select
                          value={r.accountId || ''}
                          disabled={savingRole === r.key}
                          onChange={(e) => e.target.value && changeMapping(r, e.target.value)}
                          className={`w-full h-8 px-2 text-xs bg-white border rounded-lg focus:outline-none focus:border-[#4f46e5] cursor-pointer ${
                            r.source === 'MISSING' ? 'border-rose-300 text-rose-700' : 'border-[#e2e8f0] text-[#1e293b]'
                          }`}
                        >
                          {!r.accountId && <option value="">— Select account —</option>}
                          {options.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} — {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {r.source === 'MAPPED' ? (
                          <button
                            onClick={() => resetMapping(r)}
                            disabled={savingRole === r.key}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 cursor-pointer"
                            title={r.defaultAccountName ? `Reset to default: ${r.defaultAccountName}` : 'Remove custom mapping'}
                          >
                            Custom ✕
                          </button>
                        ) : r.source === 'DEFAULT' ? (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-[#e2e8f0]">
                            Default
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                            Missing
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </div>
            ))}
          </tbody>
        </table>
      </div>

      {/* Event → Dr / Cr lines */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-800">Automatic Postings by Business Event</p>
            <p className="text-xs text-slate-400">What each transaction posts to the General Ledger</p>
          </div>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer"
          >
            {modules.map((m) => (
              <option key={m} value={m}>
                {m === 'ALL' ? 'All Modules' : m}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          {rules.events
            .filter((e) => moduleFilter === 'ALL' || e.module === moduleFilter)
            .map((ev) => (
              <div key={ev.key} className="border border-[#e2e8f0] rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-200/70 text-slate-600">{ev.module}</span>
                    <p className="text-sm font-semibold text-[#1e293b]">{ev.label}</p>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{ev.trigger}</p>
                </div>
                <div className="divide-y divide-slate-50">
                  {ev.lines.map((l, i) => {
                    const role = l.role ? roleByKey.get(l.role) : undefined;
                    return (
                      <div key={i} className="px-4 py-2 flex items-start gap-3 text-xs">
                        <span
                          className={`shrink-0 w-8 text-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                            l.side === 'DR' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700 ml-4'
                          }`}
                        >
                          {l.side}
                        </span>
                        <div className="min-w-0">
                          <p className={`font-medium ${role?.accountId || !l.role ? 'text-[#1e293b]' : 'text-rose-600'}`}>
                            {role ? accountLabel(role) : l.account || l.role}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            {role ? `${role.label} · ` : ''}
                            {l.note}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── GST Set-off ──────────────────────────────────────────────────────────────

function GstSettlementModal({
  bankAccounts,
  onClose,
  onPosted,
}: {
  bankAccounts: CoaAccount[];
  onClose: () => void;
  onPosted: () => void;
}) {
  const toast = useToast();
  const [asOfDate, setAsOfDate] = useState(todayStr());
  const [voucherDate, setVoucherDate] = useState(todayStr());
  const [bankAccountId, setBankAccountId] = useState('');
  const [preview, setPreview] = useState<GstSettlementPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    financeApi
      .getGstSettlement(asOfDate)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: any) => toast.error(err.response?.data?.message || 'Failed to compute GST set-off'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [asOfDate, toast]);

  const hasOutput = (preview?.heads || []).some((h) => h.output > 0);

  const handlePost = async () => {
    setPosting(true);
    try {
      const res = await financeApi.postGstSettlement({ asOfDate, voucherDate, bankAccountId: bankAccountId || undefined });
      toast.success(res.message);
      onPosted();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to post GST set-off');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-[#e2e8f0]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-base font-bold text-[#1e293b]">GST Set-off & Payment</h2>
            <p className="text-xs text-slate-500">Utilise input tax credit against output GST and post the net payment</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Balances as of</label>
              <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Voucher Date</label>
              <input type="date" value={voucherDate} onChange={(e) => setVoucherDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Paid From</label>
              <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)} className={inputCls}>
                <option value="">Default bank (posting rule)</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border border-[#e2e8f0] rounded-xl overflow-x-auto">
            {loading || !preview ? (
              <div className="py-10 text-center text-slate-400 text-xs">
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500 mr-2" />
                Calculating…
              </div>
            ) : (
              <table className="w-full text-xs min-w-[560px]">
                <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="text-left px-4 py-2.5">Head</th>
                    <th className="text-right px-4 py-2.5">Output GST</th>
                    <th className="text-right px-4 py-2.5">Input Credit</th>
                    <th className="text-right px-4 py-2.5">Credit Used</th>
                    <th className="text-right px-4 py-2.5">Cash Payable</th>
                    <th className="text-right px-4 py-2.5">Credit c/f</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.heads.map((h) => (
                    <tr key={h.head}>
                      <td className="px-4 py-2.5 font-semibold text-[#1e293b]">{h.head}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatINR(h.output)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatINR(h.input)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{formatINR(h.creditUsed)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-[#1e293b]">{formatINR(h.cashPayable)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-500">{formatINR(h.creditCarriedForward)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-bold">
                    <td className="px-4 py-2.5 text-[#1e293b]" colSpan={4}>
                      Net GST payable to government
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#1e293b]">{formatINR(preview.cashPayable)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {preview && preview.utilisation.length > 0 && (
            <p className="text-[11px] text-slate-500">
              Credit utilised (statutory order):{' '}
              {preview.utilisation.map((u) => `${u.from} → ${u.to} ${formatINR(u.amount)}`).join(' · ')}
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="h-9 px-4 text-sm font-medium text-slate-700 border border-[#e2e8f0] bg-white hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handlePost}
            disabled={posting || loading || !hasOutput}
            className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            {posting ? 'Posting…' : 'Post Set-off Voucher'}
          </button>
        </div>
      </div>
    </div>
  );
}
